import { Category as ApiCategory } from '@api/models';

import { ApiClient } from '@website/lib/api';
import { getAssetUrl } from '@website/utils/assets';
import { getTranslation } from '@website/utils/i18n';

export interface Category extends Omit<ApiCategory, 'name'> {
  name: string;
  photo: string;
}

/**
 * Fetches categories from the API and processes them.
 * Uses cache: 'no-store' to always fetch fresh data on page regeneration.
 *
 * @param locale The current locale string (e.g., 'en', 'fr').
 * @returns An array of Category objects. Returns empty array on API error or during check builds.
 */
export const getCategories = async (locale: string): Promise<Category[]> => {
  // Skip API call during check builds (no API available)
  if (process.env.SKIP_SSG === 'true') {
    return [];
  }

  let apiCategories: ApiCategory[];

  try {
    const response = await ApiClient.getCategories({
      authRequired: false,
      cacheTags: ['categories']
    } as RequestInit);
    apiCategories = response?.categories;
  } catch (error) {
    console.error('Failed to fetch categories:', error);
    return [];
  }

  return apiCategories.map((category) => {
    const nameString = getTranslation(category.name, locale, 'Unnamed Category');
    const photoUrl = getAssetUrl(category.imageKey);

    return {
      ...category,
      name: nameString,
      photo: photoUrl
    };
  });
};
